
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';
import JsonLd from '../../components/seo/JsonLd';
import { Link } from 'react-router-dom';

const HomePage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "GCM Mobile Notary",
    "description": "Professional mobile notary services in Sherman Oaks and San Fernando Valley",
    "url": siteUrl,
    "telephone": "+18772113011",
    "email": "office@notaryshermanoaks.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "5103 Longridge Ave",
      "addressLocality": "Sherman Oaks",
      "addressRegion": "CA",
      "postalCode": "91423",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "34.1508",
      "longitude": "-118.4456"
    },
    "openingHours": [
      "Mo-Fr 08:00-20:00",
      "Sa 09:00-18:00",
      "Su 10:00-16:00"
    ],
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "34.1508",
        "longitude": "-118.4456"
      },
      "geoRadius": "50000"
    },
    "priceRange": "$$",
    "paymentAccepted": "Cash, Check, Credit Card",
    "currenciesAccepted": "USD"
  };

  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Mobile Notary Services",
    "description": "Professional mobile notary services including Power of Attorney, Real Estate Documents, Apostille Services",
    "provider": {
      "@type": "LocalBusiness",
      "name": "GCM Mobile Notary"
    },
    "areaServed": {
      "@type": "City",
      "name": "Sherman Oaks",
      "containedInPlace": {
        "@type": "State",
        "name": "California"
      }
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Notary Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Power of Attorney Notarization"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Real Estate Document Notarization"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Apostille Services"
          }
        }
      ]
    }
  };

  const services = [
    {
      icon: 'ri-file-shield-line',
      title: 'Power of Attorney',
      description: 'Legal authorization documents for financial and healthcare decisions'
    },
    {
      icon: 'ri-home-4-line',
      title: 'Real Estate Documents',
      description: 'Property transfers, deeds, and real estate transaction notarization'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Medical Power of Attorney',
      description: 'Healthcare decision authorization for medical situations'
    },
    {
      icon: 'ri-file-text-line',
      title: 'Living Trusts & Wills',
      description: 'Estate planning documents and inheritance arrangements'
    },
    {
      icon: 'ri-passport-line',
      title: 'Travel Consent Forms',
      description: 'Authorization for minors traveling domestically or internationally'
    },
    {
      icon: 'ri-award-line',
      title: 'Apostille Services',
      description: 'Document authentication for international use and recognition'
    }
  ];

  const testimonials = [
    {
      name: 'JJ Gabriel',
      location: 'Sherman Oaks',
      text: 'Gabriel was very punctual and very professional. He worked around my schedule and answered all my questions. Would definitely recommend him to my friends and family.',
      rating: 5
    },
    {
      name: 'Steven Leigh',
      location: 'San Fernando Valley',
      text: 'Great service. Thanks Gabriel.',
      rating: 5
    },
    {
      name: 'Susy Guzman',
      location: 'Sherman Oaks',
      text: 'Excellent services, on time and very professional. Always willing to accommodate the needs and requirements of clients. Bilingual (English & Spanish) always a plus. Willing to travel. Met with this Notary on several occasions always on time, services went beyond the standards I expected. Very friendly and relaxed atmosphere yet very professional. Would recommend for any notary services 100%.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="GCM Mobile Notary - Professional Notary Services in Sherman Oaks"
        description="Professional mobile notary services in Sherman Oaks and San Fernando Valley. Power of Attorney, Real Estate, Apostille services. Same-day appointments available."
        keywords="Mobile Notary Sherman Oaks, San Fernando Valley Notary, Apostille Service near me, Traveling Notary Public, Real Estate Notary California, Power of Attorney Notary, Mobile Notary Los Angeles"
        canonical={`${siteUrl}/`}
        ogTitle="GCM Mobile Notary - Professional Notary Services in Sherman Oaks"
        ogDescription="Professional mobile notary services in Sherman Oaks and San Fernando Valley. Power of Attorney, Real Estate, Apostille services. Same-day appointments available."
        ogUrl={`${siteUrl}/`}
        ogImage={`${siteUrl}/og-image.jpg`}
      />
      <JsonLd data={organizationSchema} />
      <JsonLd data={servicesSchema} />
      
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/2790af70c742ece64486ec71ec04bfca.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Fast & Professional Mobile Notary Services in Sherman Oaks
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8">
              Reliable notary public services delivered to your location throughout the San Fernando Valley. 
              Power of Attorney, Real Estate Documents, Apostille Services & More.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:+18772113011"
                className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Call Now: (877) 211-3011
              </a>
              <Link
                to="/contact"
                className="bg-white text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                Book Appointment
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Professional Notary Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive mobile notary services throughout Sherman Oaks and the San Fernando Valley. 
              All services performed at your convenient location.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-2xl text-orange-500`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Choose Our Mobile Notary Services?
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-time-line text-orange-500"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Fast Response Time</h3>
                    <p className="text-gray-600">Same-day and emergency appointments available. We come to you within hours.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-shield-check-line text-orange-500"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Licensed & Insured</h3>
                    <p className="text-gray-600">California state certified notary public with full liability insurance coverage.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-map-pin-line text-orange-500"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Convenient Locations</h3>
                    <p className="text-gray-600">We travel to your home, office, hospital, or any location throughout the San Fernando Valley.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-user-star-line text-orange-500"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Experienced Professional</h3>
                    <p className="text-gray-600">Over 10 years of notary experience with thousands of satisfied clients.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20notary%20public%20working%20with%20client%20at%20modern%20office%20desk%2C%20reviewing%20legal%20documents%2C%20professional%20business%20attire%2C%20Sherman%20Oaks%20California%20office%20setting%2C%20natural%20lighting%2C%20trustworthy%20atmosphere%2C%20clean%20modern%20workspace&width=600&height=800&seq=why-choose-us&orientation=portrait"
                alt="Professional notary services"
                className="rounded-xl shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="py-20 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Serving the Entire San Fernando Valley
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            Based in Sherman Oaks, we provide mobile notary services throughout the San Fernando Valley and surrounding areas.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-12">
            {['Sherman Oaks', 'Encino', 'Van Nuys', 'North Hollywood', 'Studio City', 'Woodland Hills', 'Tarzana', 'Reseda', 'Canoga Park', 'Northridge', 'Granada Hills', 'Panorama City'].map((city) => (
              <div key={city} className="bg-white p-4 rounded-lg shadow-sm">
                <span className="text-gray-700 font-medium">{city}</span>
              </div>
            ))}
          </div>
          
          <Link
            to="/service-area"
            className="bg-orange-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            View Full Service Area
          </Link>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Trusted by hundreds of satisfied clients throughout the San Fernando Valley
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-xl">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-orange-400"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-gray-600">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Need Mobile Notary Services Today?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Don't waste time traveling to a notary office. We bring professional notary services directly to you 
            throughout Sherman Oaks and the San Fernando Valley.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+18772113011"
              className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (877) 211-3011
            </a>
            <Link
              to="/contact"
              className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Schedule Online
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
